import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FolderPlus } from 'react-bootstrap-icons';

import { RequirementTestFolderNode } from '../../types/requirements';

import './FolderPathSelector.css';

interface FolderPathSelectorProps {
  value: string;
  directory?: RequirementTestFolderNode | null;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

interface FolderOption {
  value: string;
  label: string;
  create?: boolean;
}

export const ROOT_FOLDER = 'Results';
export const ROOT_FOLDER_DISPLAY_NAME = 'All Test Cases';
const MAX_VISIBLE_OPTIONS = 8;

function normalizeSegment(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

export function normalizeFolderPathInput(value: string): string[] {
  const segments = value
    .split('/')
    .map(normalizeSegment)
    .filter(Boolean);

  if (segments.length === 0) {
    return [];
  }

  const firstSegment = segments[0].toLowerCase();
  if (
    firstSegment !== ROOT_FOLDER.toLowerCase() &&
    firstSegment !== ROOT_FOLDER_DISPLAY_NAME.toLowerCase()
  ) {
    return [ROOT_FOLDER, ...segments];
  }

  return [ROOT_FOLDER, ...segments.slice(1)];
}

export function formatFolderPath(path: string[]): string {
  return path.map(normalizeSegment).filter(Boolean).join(' / ');
}

export function formatFolderPathForDisplay(path?: string[] | null): string {
  const segments = (path || []).map(normalizeSegment).filter(Boolean);
  if (segments.length === 0) {
    return '';
  }

  if (segments[0].toLowerCase() === ROOT_FOLDER.toLowerCase()) {
    return [ROOT_FOLDER_DISPLAY_NAME, ...segments.slice(1)].join(' / ');
  }

  return segments.join(' / ');
}

export function collectFolderPathOptions(directory?: RequirementTestFolderNode | null): string[] {
  if (!directory) {
    return [];
  }

  const paths: string[] = [];

  function visit(node: RequirementTestFolderNode): void {
    if ((node.path || []).length > 1) {
      paths.push(formatFolderPathForDisplay(node.path));
    }
    node.children.forEach(visit);
  }

  visit(directory);
  return Array.from(new Set(paths)).sort((left, right) => left.localeCompare(right));
}

function buildOptions(directory: RequirementTestFolderNode | null | undefined, query: string): FolderOption[] {
  const baseOptions = collectFolderPathOptions(directory).map((label) => ({ value: label, label }));
  const normalizedQuery = formatFolderPathForDisplay(normalizeFolderPathInput(query));
  const lowerQuery = query.trim().toLowerCase();
  const lowerNormalizedQuery = normalizedQuery.toLowerCase();

  const matches = lowerQuery
    ? baseOptions.filter((option) => {
        const lowerLabel = option.label.toLowerCase();
        return lowerLabel.includes(lowerQuery) || lowerLabel.includes(lowerNormalizedQuery);
      })
    : baseOptions;

  if (
    normalizedQuery &&
    !baseOptions.some((option) => option.value.toLowerCase() === normalizedQuery.toLowerCase())
  ) {
    return [
      { value: normalizedQuery, label: `Create "${normalizedQuery}"`, create: true },
      ...matches,
    ];
  }

  return matches;
}

function FolderPathSelector({
  value,
  directory,
  onChange,
  disabled = false,
  placeholder = `${ROOT_FOLDER_DISPLAY_NAME} / Manual / Authentication`,
}: FolderPathSelectorProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const options = useMemo(() => buildOptions(directory, value), [directory, value]);
  const visibleOptions = options.slice(0, MAX_VISIBLE_OPTIONS);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [value, options.length]);

  useEffect(() => {
    function handleDocumentClick(event: MouseEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleDocumentClick);
    return () => document.removeEventListener('mousedown', handleDocumentClick);
  }, []);

  function handleSelect(optionValue: string): void {
    onChange(optionValue);
    setOpen(false);
  }

  function handleInputBlur(): void {
    if (!value.trim()) {
      return;
    }
    onChange(formatFolderPathForDisplay(normalizeFolderPathInput(value)));
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setHighlightedIndex((current) => Math.min(current + 1, Math.max(visibleOptions.length - 1, 0)));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((current) => Math.max(current - 1, 0));
      return;
    }

    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }

    if (event.key === 'Enter' && open && visibleOptions.length > 0) {
      event.preventDefault();
      handleSelect(visibleOptions[highlightedIndex]?.value || visibleOptions[0].value);
    }
  }

  return (
    <div className="folder-path-selector" ref={rootRef}>
      <div className="folder-path-selector-input-wrap">
        <input
          type="text"
          className="form-control form-control-sm folder-path-selector-input"
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          role="combobox"
          aria-expanded={open}
          aria-controls="folder-path-selector-options"
          aria-autocomplete="list"
        />
      </div>

      {open && !disabled && visibleOptions.length > 0 && (
        <div id="folder-path-selector-options" className="folder-path-selector-menu" role="listbox">
          {visibleOptions.map((option, index) => (
            <button
              key={`${option.create ? 'create' : 'existing'}-${option.value}`}
              type="button"
              className={`folder-path-selector-option ${index === highlightedIndex ? 'is-highlighted' : ''}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handleSelect(option.value)}
              role="option"
              aria-selected={value === option.value}
            >
              {option.create && <FolderPlus size={14} />}
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default FolderPathSelector;
